import { useRouter } from 'next/router'
import { useRef, useState } from 'react'

import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { DEFAULT_LOCATION } from '@/constants/location'
import { MAX_TOP_ADS } from '@/constants/others'
import { SRP_LISTING_LIMIT } from '@/constants/pageSettings'
import { isValidCategory } from '@/domain/category/isValidCategory'
import {
  getDateFromAppliedFilters,
  getDateRangeFromAppliedFilters,
  getRangeFromAppliedFilters,
} from '@/domain/filters'
import { getLocationArea } from '@/domain/location/getLocationArea'
import { getLocationFromSearchQuery } from '@/domain/location/getLocationFromSearchQuery'
import { isValidLocation } from '@/domain/location/isValidLocation'
import { getAttributeFromAppliedFilters } from '@/domain/srp/getAttributeFromAppliedFilters'
import { getPageOffset } from '@/domain/srp/getPageOffset'
import { sortAndShortenSrpUrl } from '@/domain/srp/getSrpUrlCacheKey'
import { mountDominantCategorySeoUrl, mountTopAdsSeoUrl, seoUrlToString } from '@/domain/urls'
import {
  type AppliedAttributeFilter,
  type AppliedDateFilter,
  type AppliedDateRangeFilter,
  type AppliedRangeFilter,
  type AttributeFilterInput,
  type DateFilterInput,
  type DateRangeFilterInput,
  type GetSearchResultsPageByUrlQuery,
  type RangeFilterInput,
  type SearchResultsByUrlInput,
  type SearchUrlInput,
  type SrpSortInput,
  AdditionalFlags,
  useGetSeoUrlLazyQuery,
} from '@/generated'
import { type GlobalLocation } from '@/lib/apollo/locationCache'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import sendToLogger from '@/utils/sendToLogger'

import { useSearchLoadingState } from './useSearchLoadingState'

export type FetchResultsParameters = {
  additionalFlagFilters?: AdditionalFlags[]
  customOffset?: number
  /**
   * Defines if the listing fetching should refine the category
   * It should only happen on search from the search bar using "All Categories"
   * */
  isDominantCategorySearch?: boolean
  newArea?: GlobalLocation
  newAttributeFilters?: AttributeFilterInput[]
  newDateRangeFilters?: DateRangeFilterInput[]
  newDateFilters?: DateFilterInput[]
  newKeyword?: string
  newPage?: number
  newRangeFilters?: RangeFilterInput[]
  newSortOption?: SrpSortInput
  selectedCategoryId?: number
  selectedLocationId?: number
  topAdCount?: number
}

export type SearchResultsPageByUrlInput = { searchResultsByUrlInput: SearchResultsByUrlInput }
export type RefetchResultsType = (
  currData?: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl'],
  params?: FetchResultsParameters,
  trackingLabel?: string
) => Promise<void>

export type SearchActionsResult = {
  refetchResults: RefetchResultsType
  attributeAppliedFilters: AppliedAttributeFilter[]
  dateAppliedFilters: AppliedDateFilter[]
  dateRangeAppliedFilters: AppliedDateRangeFilter[]
  rangeAppliedFilters: AppliedRangeFilter[]
}

export const useSearchActions = (
  filters: GetSearchResultsPageByUrlQuery['searchResultsPageByUrl']['searchQuery']['filters'] = []
): SearchActionsResult => {
  /** Helps clear debounce previous calls */
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)

  const [attributeAppliedFilters, setAttributeAppliedFilters] = useState<AppliedAttributeFilter[]>(
    getAttributeFromAppliedFilters(filters || [])
  )
  const [rangeAppliedFilters, setRangeAppliedFilters] = useState<AppliedRangeFilter[]>(
    getRangeFromAppliedFilters(filters)
  )
  const [dateRangeAppliedFilters, setDateRangeAppliedFilters] = useState<AppliedDateRangeFilter[]>(
    getDateRangeFromAppliedFilters(filters)
  )
  const [dateAppliedFilters, setDateAppliedFilters] = useState<AppliedDateFilter[]>(
    getDateFromAppliedFilters(filters)
  )

  const { setLoadingStates } = useSearchLoadingState()

  const { push } = useRouter()

  const [fetchSeoUrl] = useGetSeoUrlLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const debounce = <F extends (...args: any[]) => any>(func: F, delay: number) => {
    return function (...args: Parameters<F>) {
      timeoutIdRef.current && clearTimeout(timeoutIdRef.current)

      timeoutIdRef.current = setTimeout(() => {
        func(...args)
      }, delay)
    }
  }

  const handleRefetchDebounceCall = debounce(
    async (
      input: SearchUrlInput,
      {
        isDominantCategorySearch,
        additionalFlagFilters,
      }: Pick<FetchResultsParameters, 'isDominantCategorySearch' | 'additionalFlagFilters'>
    ) => {
      const { data } = await fetchSeoUrl({ variables: { input } })

      /**
       * Is a TopAd only search
       */
      const isTopAdsSearch = !!additionalFlagFilters?.includes(AdditionalFlags.TopAd)

      const seoUrl = data?.searchUrl

      if (!seoUrl) {
        sendToLogger('useGetSeoUrlLazyQuery returned with no data')
        return
      }

      /**
       * Cleanup URL before fetching results
       *
       * Avoid issues for the user with seoURL in case it's returned as a string instead of URL
       * */
      let redirectUrl: string = seoUrlToString(seoUrl)

      if (isDominantCategorySearch) {
        /** Append ?dc=true query parameter*/
        redirectUrl = mountDominantCategorySeoUrl(redirectUrl, ALL_CATEGORIES_ID_NUM)
      }

      if (isTopAdsSearch) {
        /** Append  ?gpTopAds='y' query parameter*/
        redirectUrl = mountTopAdsSeoUrl(redirectUrl)
      }

      /* Only push if we have generated a new url */
      if (sortAndShortenSrpUrl(window.location.toString()) === sortAndShortenSrpUrl(redirectUrl)) {
        setLoadingStates(false)
      } else {
        push(redirectUrl, undefined, { scroll: false, shallow: true })
      }
    },
    800
  )

  /**
   * While ANVIL is being updated we still have to maintain the current state of the SRP and request the SEO URL
   * before we can mutate the cache.
   */
  const refetchResults: RefetchResultsType = async (currData, props, trackingLabel) => {
    if (!currData) return

    const {
      additionalFlagFilters,
      isDominantCategorySearch,
      newArea,
      newAttributeFilters,
      newDateFilters,
      newDateRangeFilters,
      newKeyword,
      newPage = 0,
      newRangeFilters,
      newSortOption,
      selectedCategoryId,
      selectedLocationId,
      topAdCount = MAX_TOP_ADS,
    } = props || {}

    setLoadingStates({
      results: true,
      filters: !!selectedCategoryId || !!newArea || !!selectedLocationId,
    })

    /**
     * Update local values
     *
     * Attribute filters
     * Range filters
     * Date Range filters
     * Date filters
     */
    const attributeFilters =
      newAttributeFilters || getAttributeFromAppliedFilters(currData.searchQuery.filters)
    const dateFilters = newDateFilters || getDateFromAppliedFilters(currData.searchQuery.filters)
    const dateRangeFilters =
      newDateRangeFilters || getDateRangeFromAppliedFilters(currData.searchQuery.filters)
    const rangeFilters = newRangeFilters || getRangeFromAppliedFilters(currData.searchQuery.filters)

    setAttributeAppliedFilters(attributeFilters)
    setDateAppliedFilters(dateFilters)
    setDateRangeAppliedFilters(dateRangeFilters)
    setRangeAppliedFilters(rangeFilters)

    const { controls, pagination, searchQuery } = currData

    const sorting = controls.sorting.find((item) => item.isSelected)

    /**
     * Pagination
     * */
    const offset = getPageOffset(newPage, SRP_LISTING_LIMIT)
    const limit = pagination?.limit || SRP_LISTING_LIMIT

    /**
     * Sorting
     */
    const sortOption = newSortOption || sorting

    /**
     * Category
     */
    const categoryId = isDominantCategorySearch
      ? undefined
      : isValidCategory(selectedCategoryId)
      ? selectedCategoryId
      : searchQuery?.category?.id

    /**
     * Location
     */
    const currentLocation = getLocationFromSearchQuery(searchQuery)
    let locationId = newArea?.id || currentLocation?.id || DEFAULT_LOCATION.id
    let locationArea = getLocationArea(currentLocation, newArea)

    if (isValidLocation(selectedLocationId)) {
      locationId = selectedLocationId
      locationArea = undefined
    }

    /**
     * Handles keyword, included empty '' for clearing keyword
     */
    const keywords = newKeyword ?? searchQuery?.keywords

    const variables: SearchUrlInput = {
      searchQuery: {
        additionalFlagFilters: additionalFlagFilters?.length ? additionalFlagFilters : undefined,
        attributeFilters,
        categoryId,
        dateFilters,
        dateRangeFilters,
        keywords,
        location: { area: locationArea, id: locationId },
        rangeFilters,
        topAdCount,
      },
      sorting: sortOption
        ? {
            by: sortOption.by,
            direction: sortOption.direction,
          }
        : undefined,
      pagination: { offset, limit },
    }

    try {
      await handleRefetchDebounceCall(variables, {
        isDominantCategorySearch,
        additionalFlagFilters,
      })

      trackEvent({ action: GA_EVENT.FilterSelectSuccess, label: trackingLabel })
    } catch (err) {
      trackEvent({ action: GA_EVENT.FilterSelectFail })
      sendToLogger(err)
    }
  }

  return {
    refetchResults,
    attributeAppliedFilters,
    dateAppliedFilters,
    dateRangeAppliedFilters,
    rangeAppliedFilters,
  }
}
